import type { CurrentUser } from 'state/store/common/useCommonStore';
import type { IamData } from 'features/iam/services/user-service';
import type { PromptFolder, PromptItem } from './types';
import { PromptDefaultFolder, PromptVisibility } from './constants';

export function getUserDisplayName(user: CurrentUser | null): string {
  if (!user) return 'You';
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  return name || user.email || 'You';
}

export function getInitialsFromName(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase() || 'U';
}

export function getUserInitials(user: CurrentUser | null): string {
  return getInitialsFromName(getUserDisplayName(user));
}

/** Display info for a prompt/folder owner, resolved from IAM with a viewer-only fallback. */
export function getOwnerDisplay(
  ownerId: string | undefined,
  owner: IamData | undefined,
  viewer: CurrentUser | null
): { name: string; imageUrl: string | null } {
  if (owner) {
    const name = `${owner.firstName || ''} ${owner.lastName || ''}`.trim() || owner.email || owner.userName || '';
    return { name, imageUrl: owner.profileImageUrl ?? null };
  }
  if (ownerId && viewer?.id === ownerId) {
    return { name: getUserDisplayName(viewer), imageUrl: viewer?.profileImageUrl ?? null };
  }
  return { name: '', imageUrl: null };
}

/** Reject HTML markup in plain-text fields (prompt title, body, etc.). */
export function isPlainTextWithoutHtml(value: string): boolean {
  return !/<[^>]*>/.test(value);
}

/**
 * Extract variable placeholders from prompt body.
 * Supports nested double-brace syntax, e.g. "{{hi{{hello}}}}" yields "hello" and "hi{{hello}}".
 */
export function extractVariables(body: string): string[] {
  if (!body || hasInvalidPromptVariables(body)) return [];

  const variables = new Set<string>();
  let index = 0;
  const contentStarts: number[] = [];

  while (index < body.length) {
    if (body[index] === '{' && body[index + 1] === '{') {
      contentStarts.push(index + 2);
      index += 2;
      continue;
    }

    if (body[index] === '}' && body[index + 1] === '}') {
      if (contentStarts.length > 0) {
        const contentStart = contentStarts.pop()!;
        const name = body.slice(contentStart, index).trim();
        if (name) variables.add(name);
      }
      index += 2;
      continue;
    }

    index += 1;
  }

  return Array.from(variables);
}

/**
 * Prompt variables must use double-brace syntax only.
 * - No variables at all is valid (plain-text prompt).
 * - Empty placeholders like "{{}}" are invalid once a variable is added.
 * - Nested placeholders are valid, e.g. "{{hi{{hello}}}}".
 * - Single braces are invalid, e.g. "{{hi{}}}".
 */
export function hasInvalidPromptVariables(body: string): boolean {
  if (!body) return false;
  if (!body.includes('{') && !body.includes('}')) return false;

  let index = 0;
  const contentStarts: number[] = [];

  while (index < body.length) {
    if (body[index] === '{' && body[index + 1] === '{') {
      contentStarts.push(index + 2);
      index += 2;
      continue;
    }

    if (body[index] === '}' && body[index + 1] === '}') {
      if (contentStarts.length === 0) return true;

      const contentStart = contentStarts.pop()!;
      if (body.slice(contentStart, index).trim() === '') return true;

      index += 2;
      continue;
    }

    if (body[index] === '{' || body[index] === '}') {
      return true;
    }

    index += 1;
  }

  return contentStarts.length > 0;
}

export function formatVariableLabel(variable: string): string {
  if (!variable) return '';
  return variable.charAt(0).toUpperCase() + variable.slice(1);
}

export function applyPromptVariables(body: string, values: Record<string, string>): string {
  return body.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_, rawName: string) => {
    const name = rawName.trim();
    const value = values[name];
    return value !== undefined && value !== '' ? value : name;
  });
}

export function createDefaultPrivateFolder(user: CurrentUser | null): PromptFolder {
  const now = new Date().toISOString();
  return {
    id: PromptDefaultFolder.ID,
    name: PromptDefaultFolder.NAME,
    visibility: PromptVisibility.ONLY_YOU,
    isDefault: true,
    ownerId: user?.id || 'local-user',
    ownerName: getUserDisplayName(user),
    createdAt: now,
    updatedAt: now,
  };
}

export function sortFolders(folders: PromptFolder[]): PromptFolder[] {
  return [...folders].sort((a, b) => {
    if (a.isDefault && !b.isDefault) return -1;
    if (!a.isDefault && b.isDefault) return 1;
    return a.name.localeCompare(b.name);
  });
}

export function filterPromptsBySearch(prompts: PromptItem[], search: string): PromptItem[] {
  const query = search.trim().toLowerCase();
  if (!query) return prompts;
  return prompts.filter(
    (prompt) =>
      prompt.title.toLowerCase().includes(query) || prompt.body.toLowerCase().includes(query)
  );
}

export function filterFoldersBySearch(folders: PromptFolder[], search: string): PromptFolder[] {
  const query = search.trim().toLowerCase();
  if (!query) return folders;
  return folders.filter((folder) => folder.name.toLowerCase().includes(query));
}
